import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import GridItemList, { Row } from 'components/GridItemList';
import { differenceInDays } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode } from 'react';
import { ContactDto } from 'types/contacts';
import { t } from 'utils/getTranslations';
import {
    ComplianceSummaryState,
    formatDateValue,
    getBlockingPolicyReadonlyState,
    parseLocalDate,
    SourceRowState,
    startOfToday,
} from './BlockingPolicyReadonlyShared';

interface BlockingPolicyReadonlySimpleProps {
    contact: ContactDto;
    /** The new design's section card around the rows: its title and Edit. */
    card?: { titleId: string; button: ReactNode };
}

const RowList = ({ data, card }: { data: Row[]; card?: BlockingPolicyReadonlySimpleProps['card'] }) =>
    card ? (
        <GridItemList isNew titleId={card.titleId} button={card.button} data={data} />
    ) : (
        <GridItemList noContainer shouldTranslate={false} size={16} data={data} />
    );

const chip = (label: string, type: ChipType) => <StatusChip label={label} type={type} />;

const getManualValue = (manual: SourceRowState): React.ReactNode =>
    manual.enforcing ? chip(t('contacts.blocking.manual-block-applied'), 'error') : '-';

const getComplianceValue = (
    compliance: ComplianceSummaryState,
    dateFormat: string,
    enforcingOverride?: boolean
): React.ReactNode => {
    if (compliance.isIgnored) {
        return chip(t('contacts.blocking.ignored'), 'warning');
    }

    if (compliance.planned && compliance.blockedFrom) {
        return chip(
            t('contacts.blocking.ignored-until-date', { date: formatDateValue(compliance.blockedFrom, dateFormat) }),
            'warning'
        );
    }

    if (enforcingOverride ?? compliance.enforcing) {
        return chip(t('contacts.blocking.compliance-block-applied'), 'error');
    }

    return '-';
};

const getDebtValue = (debt: SourceRowState, dateFormat: string): React.ReactNode => {
    if (debt.isIgnored) {
        return chip(t('contacts.blocking.debt-block-excluded'), 'info');
    }

    if (debt.enforcing) {
        return chip(t('contacts.blocking.debt-block-applied'), 'error');
    }

    if (debt.planned && debt.blockedFrom) {
        const days = differenceInDays(parseLocalDate(debt.blockedFrom), startOfToday());
        return chip(
            t('contacts.blocking.debt-block-planned', {
                days: String(days),
                date: formatDateValue(debt.blockedFrom, dateFormat),
            }),
            'warning'
        );
    }

    return '-';
};

const BlockingCasesReadonlySimple: React.FC<BlockingPolicyReadonlySimpleProps> = ({ contact, card }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const [casesPolicy] = getBlockingPolicyReadonlyState(contact);

    const casesRows: Row[] = [
        {
            label: t('contacts.blocking.source.manual'),
            content: getManualValue(casesPolicy.manual),
        },
        {
            label: t('contacts.blocking.source.compliance'),
            content: getComplianceValue(casesPolicy.compliance, effectiveFormat),
        },
    ];

    return <RowList data={casesRows} card={card} />;
};

const BlockingTimecardsReadonlySimple: React.FC<BlockingPolicyReadonlySimpleProps> = ({ contact, card }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const [, timecardsPolicy] = getBlockingPolicyReadonlyState(contact);

    const timecardsRows: Row[] = [
        {
            label: t('contacts.blocking.source.manual'),
            content: getManualValue(timecardsPolicy.manual),
        },
        {
            label: t('contacts.blocking.source.compliance'),
            content: getComplianceValue(timecardsPolicy.compliance, effectiveFormat),
        },
        {
            label: t('contacts.blocking.source.debt'),
            content: timecardsPolicy.debt ? getDebtValue(timecardsPolicy.debt, effectiveFormat) : '-',
        },
    ];

    return <RowList data={timecardsRows} card={card} />;
};

const BlockingAmlTimecardsReadonlySimple: React.FC<BlockingPolicyReadonlySimpleProps> = ({ contact, card }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const [, , amlTimecardsPolicy] = getBlockingPolicyReadonlyState(contact);

    const amlTimecardsRows: Row[] = [
        {
            label: t('contacts.blocking.source.compliance'),
            content: getComplianceValue(
                amlTimecardsPolicy.compliance,
                effectiveFormat,
                contact.isAmlTimecardsBlockActive ?? false
            ),
        },
    ];

    return <RowList data={amlTimecardsRows} card={card} />;
};

export { BlockingAmlTimecardsReadonlySimple, BlockingCasesReadonlySimple, BlockingTimecardsReadonlySimple };
